// ---------------------------------------------------------------------------
// Page-URL resolution — preview-proxy aware.
// ---------------------------------------------------------------------------
// Normally the widget reports `window.location` to the server (manifest
// lookups, cache keys, persist). But inside the dashboard's live-preview
// proxy the widget runs on the worker origin
// (api.goaccessify.de/v1/preview/proxy?…) — so
// window.location points at the proxy endpoint, not the customer page.
// Manifest lookups for that URL would always miss.
//
// The proxy injects `window.__ACCESSIFY_PREVIEW_URL` with the real
// customer URL. When that global is present, every server-facing URL the
// widget builds must use it instead of window.location.
// ---------------------------------------------------------------------------

interface PageLocation {
  origin: string;
  pathname: string;
  href: string;
}

/** Resolve the URL the widget should report to the server. Returns the
 *  injected preview URL when running inside the dashboard preview proxy,
 *  otherwise the real window.location. */
export function pageLocation(): PageLocation {
  if (typeof window !== 'undefined') {
    const preview = (window as unknown as { __ACCESSIFY_PREVIEW_URL?: unknown }).__ACCESSIFY_PREVIEW_URL;
    if (typeof preview === 'string' && preview) {
      try {
        const u = new URL(preview);
        return { origin: u.origin, pathname: u.pathname, href: u.href };
      } catch {
        /* malformed — fall through to real location */
      }
    }
  }
  return {
    origin: window.location.origin,
    pathname: window.location.pathname,
    href: window.location.href,
  };
}

/** Convenience: origin + pathname, the canonical "page URL" form used for
 *  manifest keys and the manifest `url` query parameter. */
export function pageUrl(): string {
  const loc = pageLocation();
  return loc.origin + loc.pathname;
}

/** True when running inside the dashboard's live-preview proxy (the proxy
 *  injects `__ACCESSIFY_PREVIEW_URL`). Used to keep per-VISITOR runtime state —
 *  e.g. a dragged panel position — from leaking into the OWNER's preview, so
 *  the preview always shows the configured default (docked) position. */
export function isPreviewContext(): boolean {
  if (typeof window === 'undefined') return false;
  const preview = (window as unknown as { __ACCESSIFY_PREVIEW_URL?: unknown }).__ACCESSIFY_PREVIEW_URL;
  return typeof preview === 'string' && preview.length > 0;
}
